import { Component } from 'react';
import { connect, Dispatch } from "react-redux";
import { bindActionCreators } from "redux";
import {
	RouterProps,
	withRouter,
	useAuthCheck,
	getItemActionRoute,
	getUserActiveMarketActionRoute
} from "utils/route";
import { ApplicationState } from 'reducers/types';
import { ItemActions } from 'reducers/Item/actions';
import { AuthActions } from 'reducers/Auth/actions';
import { UserActions } from 'reducers/User/actions';
import { UserSelectors } from 'reducers/User/selectors';
import { AuthSelectors } from 'reducers/Auth/selectors';
import {
	Box,
	Stack
} from '@chakra-ui/react';
import {
	TradingFloor,
	Hero,
	SEOHelmet,
	Modal,
	LoginCard,
	ItemBidContactForm,
	ItemAskContactForm
} from 'components';
import { UserModel, ItemBidModel, ItemAskModel } from 'models';
import { NavigationService } from 'services';
import { AppConstants } from '../../constants';
import { ItemActiveMarketData } from 'models/Item/types';
import { ActiveMarketActions } from 'reducers/ActiveMarket/actions';
import { ActiveMarketSelectors } from 'reducers/ActiveMarket/selectors';
import { generateSEOLink, generateSEOTitle } from 'utils/seo';
import { JSONObject } from 'models/Api/types';


interface TradingFloorViewProps extends RouterProps {
	user: UserModel;
	userLoading: boolean;
	allActiveMarket: ItemActiveMarketData;
	allActiveMarketLoading: boolean;
	loginLoading: boolean;
	forgotPasswordLoading: boolean;
	placeItemInquiry: (itemId: string, inquiryData: JSONObject) => void;
	loginByEmail: (email: string, password: string) => void;
	sendForgotPasswordEmail: (email: string) => void;

	getAllActiveMarket: () => void;
}

interface TradingFloorViewModalState {
	contactItemBid: ItemBidModel | undefined;
	contactItemAsk: ItemAskModel | undefined;
	login: boolean;
}


interface TradingFloorViewState {
	modals: TradingFloorViewModalState;
}

class TradingFloorBase extends Component<TradingFloorViewProps, TradingFloorViewState> {
	state: TradingFloorViewState = {
		modals: {
			contactItemBid: undefined,
			contactItemAsk: undefined,
			login: false
		}
	};

	componentDidMount() {
		const { getAllActiveMarket } = this.props;
		getAllActiveMarket();
	}

	onItemBidEditClick = (itemBid: ItemBidModel) => {
		const { user, navigate } = this.props;
		navigate(getUserActiveMarketActionRoute(user, itemBid, 'Edit'));
	};

	onItemBidRemoveClick = (itemBid: ItemBidModel) => {
		const { user, navigate } = this.props;
		navigate(getUserActiveMarketActionRoute(user, itemBid, 'Remove'));
	};

	onItemAskEditClick = (itemAsk: ItemAskModel) => {
		const { user, navigate } = this.props;
		navigate(getUserActiveMarketActionRoute(user, itemAsk, 'Edit'));
	};

	onItemAskRemoveClick = (itemAsk: ItemAskModel) => {
		const { user, navigate } = this.props;
		navigate(getUserActiveMarketActionRoute(user, itemAsk, 'Remove'));
	};

	onItemBidContactClick = (itemBid: ItemBidModel) => {
		const { user, navigate } = this.props;
		useAuthCheck(
			user,
			navigate,
			() => {
				this.setState({
					modals: {
						...this.state.modals,
						contactItemBid: itemBid
					}
				});
			},
			{ onUnauthenticated: this.onLoginModalOpen }
		);
	};

	onItemAskContactClick = (itemAsk: ItemAskModel) => {
		const { user, navigate } = this.props;
		useAuthCheck(
			user,
			navigate,
			() => {
				this.setState({
					modals: {
						...this.state.modals,
						contactItemAsk: itemAsk
					}
				});
			},
			{ onUnauthenticated: this.onLoginModalOpen }
		);
	};

	onContactItemBidModalClose = () => {
		this.setState({
			modals: {
				...this.state.modals,
				contactItemBid: undefined
			}
		});
	};

	onContactItemAskModalClose = () => {
		this.setState({
			modals: {
				...this.state.modals,
				contactItemAsk: undefined
			}
		});
	};

	onContactItemBidFormSubmit = (itemBid: ItemBidModel, message: string) => {
		const { placeItemInquiry } = this.props;
		const itemId = itemBid.itemId;
		const inquiryData: JSONObject = {
			item_id: itemId,
			entity_id: itemBid.id,
			entity_type: 'Bid',
			message
		};
		placeItemInquiry(itemId, inquiryData);
		this.onContactItemBidModalClose();
	};

	onContactItemAskFormSubmit = (itemAsk: ItemAskModel, message: string) => {
		const { placeItemInquiry } = this.props;
		const itemId = itemAsk.itemId;
		const inquiryData: JSONObject = {
			item_id: itemId,
			entity_id: itemAsk.id,
			entity_type: 'Ask',
			message
		};
		placeItemInquiry(itemId, inquiryData);
		this.onContactItemAskModalClose();
	};

	// -------- Login modal --------
	onLoginModalOpen = () => {
		this.setState({
			modals: {
				...this.state.modals,
				login: true
			}
		});
	};

	onLoginModalClose = () => {
		this.setState({
			modals: {
				...this.state.modals,
				login: false
			}
		});
	};

	onLoginModalSubmit = (email: string, password: string) => {
		const { loginByEmail } = this.props;
		loginByEmail(email, password);
		this.onLoginModalClose();
	};

	onLoginModalForgetPasswordSubmit = (email: string) => {
		const { sendForgotPasswordEmail } = this.props;
		sendForgotPasswordEmail(email);
		this.onLoginModalClose();
	};

	// -------- UI sections --------
	renderTradingFloor() {
		const { user, userLoading, allActiveMarket, allActiveMarketLoading, navigate } = this.props;
		return (
			<Box padding={{ base: "10px", md: "0px" }} marginTop={8}>
				<TradingFloor
					asks={allActiveMarket.asks}
					bids={allActiveMarket.bids}
					user={user}
					showSearchFilter={true}
					onItemNameClick={(id: string, name: string) =>
						navigate(NavigationService.getItemDetailsPath(id, name))
					}
					onItemArtistNameClick={(artistId: string, artistName: string) =>
						navigate(NavigationService.getArtistDetailsPath(artistId, artistName))
					}
					isLoading={userLoading || allActiveMarketLoading}
					onEditBidClick={this.onItemBidEditClick}
					onRemoveBidClick={this.onItemBidRemoveClick}
					onEditAskClick={this.onItemAskEditClick}
					onRemoveAskClick={this.onItemAskRemoveClick}
					onContactBidClick={this.onItemBidContactClick}
					onContactAskClick={this.onItemAskContactClick}
					onSellBidClick={(itemBid: ItemBidModel) =>
						navigate(getItemActionRoute(user, itemBid, 'Sell'))
					}
					onBuyAskClick={(itemAsk: ItemAskModel) =>
						navigate(getItemActionRoute(user, itemAsk, 'Buy'))
					}
				/>
			</Box>
		);
	}

	renderHero() {
		return (
			<Hero
				title="The Art Market: Redefined"
				subtitle='Fueled with efficiency and transparency, our Trading Floor gives visibility to the best deals in our network with complete ease.'
				image="https://storage.googleapis.com/artcore-image-set/content-images/Beige%20Artist%208%20Grid%20Collage.png"
			/>
		);
	}

	renderContactItemAskModalContent() {
		const { modals } = this.state;
		if (modals.contactItemAsk && modals.contactItemAsk.item) {
			return (
				<ItemAskContactForm item={modals.contactItemAsk.item} itemAsk={modals.contactItemAsk} onSubmit={this.onContactItemAskFormSubmit} />
			);
		} else {
			return <Box />;
		}
	}

	renderContactItemBidModalContent() {
		const { modals } = this.state;
		if (modals.contactItemBid && modals.contactItemBid.item) {
			return (
				<ItemBidContactForm item={modals.contactItemBid.item} itemBid={modals.contactItemBid} onSubmit={this.onContactItemBidFormSubmit} />
			);
		} else {
			return <Box />;
		}
	}

	renderLoginModalContent() {
		const { loginLoading, forgotPasswordLoading } = this.props;
		const { modals } = this.state;
		if (modals.login) {
			return (
				<LoginCard
					onLoginClick={this.onLoginModalSubmit}
					onForgotPasswordClick={this.onLoginModalForgetPasswordSubmit}
					submitLoading={loginLoading || forgotPasswordLoading}
				/>
			);
		} else {
			return <Box />;
		}
	}

	renderModals() {
		const { modals } = this.state;
		return (
			<Stack>
				<Modal
					title={'Submit Inqury'}
					isOpen={!!modals.contactItemBid}
					onClose={this.onContactItemBidModalClose}
					content={this.renderContactItemBidModalContent()}
					showCloseButton={false}
					showSubmitButton={false}
				/>
				<Modal
					title={'Submit Inqury'}
					isOpen={!!modals.contactItemAsk}
					onClose={this.onContactItemAskModalClose}
					content={this.renderContactItemAskModalContent()}
					showCloseButton={false}
					showSubmitButton={false}
				/>
				<Modal
					title={'Log In'}
					isOpen={!!modals.login}
					onClose={this.onLoginModalClose}
					content={this.renderLoginModalContent()}
					showCloseButton={false}
					showSubmitButton={false}
					variant={'takeover'}
				/>
			</Stack>
		);
	}

	render() {
		return (
			<>
				<SEOHelmet
					title={generateSEOTitle('Trading Floor')}
					description={`Discover the best investment-grade art market deals with effeciency and transparency provided by our valuation engine.`}
					link={generateSEOLink(NavigationService.getTradingFloorPath())}
				/>
				<Box
					maxWidth={`${AppConstants.GRIDPAGE_WIDTH}px`}
					paddingTop={['80px', '100px', '100px']}
					justifySelf="center"
					minWidth={['100%', `${AppConstants.GRIDPAGE_WIDTH}px`]}
				>
					{this.renderHero()}
					{this.renderTradingFloor()}
					{this.renderModals()}
				</Box>
			</>
		);
	}
}

function mapStateToProps(state: ApplicationState) {
	return {
		user: UserSelectors.getUser(state),
		userLoading: UserSelectors.getUserLoading(state) || AuthSelectors.getUserLoading(state),
		allActiveMarket: ActiveMarketSelectors.getAllActiveMarket(state),
		allActiveMarketLoading: ActiveMarketSelectors.getAllActiveMarketLoading(state),

		// Login modal spinners (same shape you used in Item Details)
		loginLoading: AuthSelectors.getUserLoginLoading(state),
		forgotPasswordLoading: UserSelectors.getUserMailActionLoading(state)
	};
}

function mapDispatchToProps(dispatch: Dispatch<ApplicationState>) {
	return bindActionCreators(
		{
			getAllActiveMarket: () => ActiveMarketActions.getAllActiveMarket(),

			// Contact submit + login modal actions
			placeItemInquiry: (itemId: string, inquiryData: JSONObject) => ItemActions.placeItemInquiry(itemId, inquiryData),
			loginByEmail: (email: string, password: string) => AuthActions.loginByEmail(email, password),
			sendForgotPasswordEmail: (email: string) => UserActions.sendForgotPasswordEmail(email)
		},
		dispatch
	);
}

export const TradingFloorView = connect(
	mapStateToProps,
	mapDispatchToProps
)(withRouter(TradingFloorBase));
